import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuditStatus } from '@/hooks/use-asset-management-widgets';
import { ClipboardCheck, Calendar, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export function AuditStatusWidget() {
  const { data, isLoading, error } = useAuditStatus();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Audit Status</CardTitle>
              <CardDescription>Active audit progress</CardDescription>
            </div>
            <div className="p-2 bg-purple-50 dark:bg-purple-950 rounded-lg">
              <ClipboardCheck className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Handle error state (API failure)
  if (error) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Audit Status</CardTitle>
              <CardDescription>Active audit progress</CardDescription>
            </div>
            <div className="p-2 bg-purple-50 dark:bg-purple-950 rounded-lg">
              <ClipboardCheck className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            Unable to load audit data
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle empty data state (no audits in database)
  if (!data || data.totalActiveAudits === 0) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Audit Status</CardTitle>
              <CardDescription>Active audit progress</CardDescription>
            </div>
            <div className="p-2 bg-purple-50 dark:bg-purple-950 rounded-lg">
              <ClipboardCheck className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex flex-col h-[300px] items-center justify-center text-center">
            <ClipboardCheck className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              No active audits
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleAuditClick = (auditId: string) => {
    navigate(`/audits/${auditId}`);
  };

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col')}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Audit Status</CardTitle>
            <CardDescription>Active audit progress</CardDescription>
          </div>
          <div className="p-2 bg-purple-50 dark:bg-purple-950 rounded-lg">
            <ClipboardCheck className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {/* Two Column Layout */}
        <div className="grid grid-cols-2 gap-6">
          {/* Left Column: Stats & Metrics */}
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-2 gap-2">
              <div className="text-center p-3 rounded-lg border bg-card">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{data.totalActiveAudits}</div>
                <div className="text-xs text-muted-foreground mt-1">Active Audits</div>
              </div>
              <div className="text-center p-3 rounded-lg border bg-card">
                <div className="text-2xl font-bold">{data.averageCompletion.toFixed(0)}%</div>
                <div className="text-xs text-muted-foreground mt-1">Avg Progress</div>
              </div>
            </div>

            {/* Additional Metrics */}
            <div className="space-y-2">
              <div className="p-3 rounded-lg border bg-card">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Items Overdue</span>
                  <span className="text-xl font-bold text-red-600 dark:text-red-400">
                    {data.itemsOverdue ?? 0}
                  </span>
                </div>
              </div>

              <div className="p-3 rounded-lg border bg-card">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Exceptions This Week</span>
                  <span className="text-xl font-bold text-amber-600 dark:text-amber-400">
                    {data.exceptionsThisWeek ?? 0}
                  </span>
                </div>
              </div>

              <div className="p-3 rounded-lg border bg-card">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Completed This Month</span>
                  <span className="text-xl font-bold text-green-600 dark:text-green-400">
                    {data.completedThisMonth ?? 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Recent Audits List */}
          <div className="space-y-3">
            <div className="text-sm font-semibold">Recent Audits</div>
            
            {data.activeAudits.length > 0 ? (
              <div className="space-y-2 max-h-[280px] overflow-y-auto pr-2">
                {data.activeAudits.slice(0, 4).map((audit) => (
                  <button
                    type="button"
                    key={audit.auditId}
                    className="p-2.5 rounded-lg border bg-card hover:shadow-sm transition-shadow cursor-pointer w-full text-left"
                    onClick={() => handleAuditClick(audit.auditId)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{audit.auditName}</div>
                        <div className="text-xs text-muted-foreground">{audit.totalAssets} assets</div>
                      </div>
                      <div className="text-xs font-bold text-purple-600 dark:text-purple-400 whitespace-nowrap">
                        {audit.completionPercentage.toFixed(0)}%
                      </div>
                    </div>
                    {audit.dueDate && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span>{format(new Date(audit.dueDate), 'dd MMM yyyy')}</span>
                        {audit.isOverdue && (
                          <Badge variant="destructive" className="h-4 text-[10px] py-0 ml-auto">
                            Overdue
                          </Badge>
                        )}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground py-8 text-center">
                No active audits
              </div>
            )}

            {data.activeAudits.length > 0 && (
              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => navigate('/audits')}
                >
                  View All Audits
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
